import { X } from "lucide-react";
import { useEffect, useId, useRef } from "react";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Where Tab goes inside a dialog with `count` focusable elements, from the one at `index` (-1 when
 * focus is on none of them). Past the last it wraps to the first, and Shift+Tab before the first to
 * the last, so focus never leaves the dialog for the page behind it. Null with nothing to focus.
 */
export function nextFocusIndex(count: number, index: number, shift: boolean): number | null {
  if (count === 0) return null;
  if (index < 0) return shift ? count - 1 : 0;
  return shift ? (index - 1 + count) % count : (index + 1) % count;
}

/**
 * A modal dialog: labelled by its title, focus moved in on open, held inside while it is open,
 * closed by Escape or the backdrop, and given back to whatever opened it.
 */
export function Dialog({
  title, onClose, children, className = "", headExtra,
}: {
  title: React.ReactNode; onClose: () => void; children: React.ReactNode; className?: string;
  /** Controls beside the title, before the close button. */
  headExtra?: React.ReactNode;
}) {
  const titleId = useId();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = box.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? box.current)?.focus();
    return () => { if (opener && document.contains(opener)) opener.focus(); };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); close.current(); return; }
    if (e.key !== "Tab" || !box.current) return;
    const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const next = nextFocusIndex(items.length, items.indexOf(document.activeElement as HTMLElement), e.shiftKey);
    e.preventDefault();
    if (next !== null) items[next].focus();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div ref={box} className={`modal ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby={titleId}
           tabIndex={-1} onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div className="head">
          <strong id={titleId}>{title}</strong>
          {headExtra}
          <button type="button" className="ghost iconbtn-sm" style={headExtra ? undefined : { marginLeft: "auto" }}
                  onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
